// ═══════════════════════════════════════════════════════════════
// SendToVaultModal — Confirmation d'envoi d'un bloc au coffre-fort
// ═══════════════════════════════════════════════════════════════
//
// Détecte automatiquement le mode du coffre :
//   - V1 standard  → POST plaintext, le serveur chiffre avec scrypt user
//   - V2 renforcé  → chiffre côté navigateur avec le DEK déwrappé,
//                    POST envelope opaque (le serveur n'a jamais le plaintext)
//
// Si V2 actif mais coffre verrouillé : invite à déverrouiller d'abord.
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { Lock, X, ShieldCheck, Unlock } from 'lucide-react';
import { sendToVault, VaultApiError } from '../../services/vaultApi';
import { encryptItem } from '../../utils/vaultZkCrypto';
import { useVaultZk } from './VaultZkContext';
import { toast } from '../ui/Toast';

/**
 * Construit un titre lisible depuis les premiers mots significatifs du contenu.
 * Strip markdown bullets/headers/tables, garde 50 chars max, append HHMMSS.
 * Permet de distinguer au listing les messages frères ("Bloc B2a livré" vs "B2b").
 */
function buildVaultTitle(content) {
    if (typeof content !== 'string' || !content.trim()) {
        return `vault-item-${formatHMS(new Date())}.txt`;
    }
    // Retire markdown commun + URL + emojis + caractères de structure
    const cleaned = content
        .replace(/```[\s\S]*?```/g, ' ')           // blocs de code
        .replace(/`[^`]*`/g, ' ')                  // code inline
        .replace(/\!\[[^\]]*\]\([^)]*\)/g, ' ')    // images
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')   // liens → texte
        .replace(/^[#>\s\-*|_=]+/gm, ' ')          // bullets/headers/tables
        .replace(/[*_~`#>]/g, ' ')                 // emphasis
        .replace(/\|+/g, ' ')                      // pipes table
        .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, ' ') // emojis BMP+SMP
        .replace(/\s+/g, ' ')
        .trim();
    if (!cleaned) return `vault-item-${formatHMS(new Date())}.txt`;
    // Slug : 50 chars max, garde lettres/chiffres/accents/espaces
    let slug = cleaned.slice(0, 60);
    // Coupe au dernier espace si le slug s'arrête en plein mot
    if (cleaned.length > 60) {
        const lastSpace = slug.lastIndexOf(' ');
        if (lastSpace > 30) slug = slug.slice(0, lastSpace);
    }
    // Sanitize filesystem-safe : enlève / \ : * ? " < > |
    slug = slug.replace(/[/\\:*?"<>|]/g, '-').trim();
    return `${slug} — ${formatHMS(new Date())}.txt`;
}

function formatHMS(d) {
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getHours())}h${p(d.getMinutes())}`;
}

const SendToVaultModal = ({ open, onClose, projectId, conversationId, role, content, onConfirmed }) => {
    const { challenge, isUnlocked, dek } = useVaultZk();
    const [loading, setLoading] = useState(false);

    if (!open) return null;

    const zkActive = challenge?.vault_zk_enabled === true;
    const blockedByLock = zkActive && !isUnlocked;

    const handleConfirm = async () => {
        if (loading || blockedByLock) return;
        setLoading(true);
        try {
            // Slug lisible depuis les premiers mots — résout la confusion
            // entre messages frères (B2a / B2b / etc.) au listing.
            // Côté V1 c'est juste un naming. Côté V2 le titre est lisible
            // serveur mais le contenu reste strictement chiffré (pattern 1Password).
            const displayTitle = buildVaultTitle(content);
            let payload;
            if (zkActive && isUnlocked && dek) {
                // ─── V2 zero-knowledge : chiffre côté navigateur ───
                const envelope = await encryptItem(content, dek);
                const dot = envelope.indexOf('.');
                const iv = envelope.slice(0, dot);
                const ciphertext_b64 = envelope.slice(dot + 1);
                payload = {
                    projectId,
                    sourceConversationId: conversationId || null,
                    role: role || null,
                    displayTitle,
                    vaultZkEnabled: true,
                    envelopeV2: { iv, ciphertext_b64, sizeChars: content.length },
                };
            } else {
                // ─── V1 standard : plaintext, chiffrement serveur ───
                payload = {
                    projectId,
                    sourceConversationId: conversationId || null,
                    role: role || null,
                    displayTitle,
                    content,
                };
            }
            const result = await sendToVault(payload);
            toast.success(zkActive
                ? 'Envoyé au coffre-fort en mode renforcé. L\'IA n\'y a plus accès — et le serveur non plus.'
                : 'Envoyé au coffre-fort. L\'IA n\'y a plus accès.'
            );
            onConfirmed?.(result);
            onClose?.();
        } catch (e) {
            if (e instanceof VaultApiError && e.status === 409 && e.code === 'already_in_vault') {
                toast.info('Ce bloc est déjà au coffre-fort.');
                onConfirmed?.({ alreadyInVault: true });
                onClose?.();
            } else if (e instanceof VaultApiError && e.status === 423) {
                toast.error('Coffre-fort temporairement verrouillé suite à des tentatives d\'accès. Réessayez plus tard.');
            } else if (e instanceof VaultApiError && e.status === 410) {
                toast.error('Coffre-fort détruit (auto-destruction déclenchée). Contactez le support.');
            } else {
                toast.error('Erreur : ' + (e.message || 'inconnue'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(4px)', zIndex: 10000,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'fadeIn 0.18s ease',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: 12,
                    padding: '24px 26px',
                    width: 'min(460px, 92vw)',
                    boxShadow: '0 24px 56px rgba(0,0,0,0.4)',
                    color: 'var(--text)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 9,
                        background: 'linear-gradient(135deg, rgba(167,139,250,0.18), rgba(192,132,252,0.10))',
                        border: '1px solid rgba(167,139,250,0.32)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Lock size={17} style={{ color: '#a78bfa' }} />
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>Envoyer au coffre-fort</div>
                    {zkActive && (
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '3px 9px', borderRadius: 99,
                            background: 'rgba(34, 197, 94, 0.12)', color: '#22c55e',
                            fontSize: 11, fontWeight: 500, marginLeft: 4,
                        }}>
                            <ShieldCheck size={11} />
                            Mode renforcé
                        </span>
                    )}
                    <button
                        onClick={onClose}
                        style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                        aria-label="Fermer"
                    >
                        <X size={16} />
                    </button>
                </div>

                <p style={{ margin: '0 0 16px', fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-secondary)' }}>
                    {zkActive ? (
                        <>
                            Ce contenu sera <strong style={{ color: 'var(--text)' }}>chiffré dans
                            ce navigateur avec votre clé maître</strong>, puis envoyé sous forme d'envelope
                            opaque. Ni l'IA, ni le serveur, ni un administrateur ne peut le lire — seul vous.
                        </>
                    ) : (
                        <>
                            Ce contenu sera <strong style={{ color: 'var(--text)' }}>chiffré et retiré
                            de la mémoire de l'IA</strong> (résumés, recherche sémantique, profil,
                            insights trans-projet). Seul vous pourrez le relire depuis votre coffre-fort.
                        </>
                    )}
                </p>

                {blockedByLock && (
                    <div style={{
                        margin: '0 0 14px', padding: '11px 13px',
                        background: 'rgba(245, 158, 11, 0.08)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderRadius: 9,
                        fontSize: 12.5, lineHeight: 1.5, color: '#f59e0b',
                        display: 'flex', gap: 9, alignItems: 'flex-start',
                    }}>
                        <Unlock size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>
                            Votre coffre-fort est verrouillé dans cet onglet. Allez sur
                            <strong> Coffre-fort → Paramètres → Déverrouiller maintenant </strong>
                            puis revenez ici pour envoyer en mode renforcé.
                        </span>
                    </div>
                )}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        style={{
                            background: 'var(--input-bg)',
                            border: '1px solid var(--card-border)',
                            color: 'var(--text-secondary)',
                            padding: '8px 16px', borderRadius: 8,
                            cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13,
                        }}
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading || blockedByLock}
                        style={{
                            background: blockedByLock ? '#6a6a85' : '#a78bfa',
                            border: 'none', color: 'white',
                            padding: '8px 18px', borderRadius: 8,
                            cursor: (loading || blockedByLock) ? 'not-allowed' : 'pointer',
                            fontSize: 13, fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: 6,
                            opacity: (loading || blockedByLock) ? 0.7 : 1,
                        }}
                    >
                        <Lock size={13} />
                        {loading ? 'Chiffrement…' : zkActive ? 'Envoyer (mode renforcé)' : 'Envoyer au coffre'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SendToVaultModal;
